import React, { useState, useCallback, useEffect } from 'react';
import LiveStreamPlayer from './components/LiveStreamPlayer';
import './MovableFrame.scss';

const BACKEND_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:3001';

export default function LiveView({ isOpen, onClose, unitId, unitName }) {
  const [position, setPosition] = useState({ 
    x: window.innerWidth * 0.05, 
    y: window.innerHeight * 0.05 
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [unitData, setUnitData] = useState(null);

  const [testOutput, setTestOutput] = useState('');

  const runTest = async () => {
    setTestOutput('Running...');
    try {
      const res = await fetch(`${BACKEND_URL}/raspberry/execute`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ script: 'test.sh' })
      });
      const data = await res.json();
      setTestOutput(data.stdout || data.error || JSON.stringify(data));
    } catch (e) {
      setTestOutput(`Error: ${e.message}`);
    }
  };

  const openVNC = () => {
    window.open(`${BACKEND_URL}/novnc`, '_blank');
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.frame-content')) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  useEffect(() => {
    if (!isOpen) return;
    
    const fetchUnitData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/units/my-units/data`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        const unit = data.find(u => u.id === unitId);
        setUnitData(unit);
      } catch (err) {
        console.error('Failed to fetch unit data:', err);
      }
    };
    
    fetchUnitData();
    const interval = setInterval(fetchUnitData, 2000);
    return () => clearInterval(interval);
  }, [isOpen, unitId]);

  if (!isOpen) return null;

  return (
    <div
      className="movable-frame unified-frame"
      style={{
        left: position.x,
        top: position.y,
        cursor: isDragging ? 'grabbing' : 'grab',
        width: '90vw',
        height: '90vh'
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="frame-header">
        <span>Live View - {unitName || `Unit ${unitId}`}</span>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>
      <div className="frame-content" style={{ padding: '16px', overflowY: 'auto' }}>
        <div className="left-panel">
          <h6 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold' }}>Unit Data</h6>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div><strong>Position:</strong> {unitData?.latitude?.toFixed(6) || 'N/A'}, {unitData?.longitude?.toFixed(6) || 'N/A'}</div>
            <div><strong>Speed:</strong> {unitData?.speed?.toFixed(1) || 'N/A'} km/h</div>
            <div><strong>Heading:</strong> {unitData?.heading?.toFixed(0) || 'N/A'}°</div>
            <div><strong>Last Update:</strong> {unitData?.lastUpdate ? new Date(unitData.lastUpdate).toLocaleTimeString() : 'N/A'}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
            <button className="action-btn action-btn--green" onClick={runTest}>Run Test</button>
            {testOutput && <div className="test-output">{testOutput}</div>}
            <button className="action-btn action-btn--grey" onClick={openVNC}>Remote Desktop</button>
            <button className="action-btn action-btn--blue disabled" disabled>Record Session</button>
          </div>
        </div>
        <div className="right-panel">
          <LiveStreamPlayer style={{ width: '50%' }} unitId={unitId} />
        </div>

        
      </div>
    </div>
  );
}
